import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase.js';
import posthog from 'posthog-js';
import { CreditCard, ArrowLeft, Check, X } from 'lucide-react';
import { usePlan } from '../context/PlanContext.jsx';
import styles from './BillingSettings.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

const PLANS = {
  free:    { name: 'Free',    priceLabel: '£0/mo'      },
  indie:   { name: 'Indie',   priceLabel: '£29/mo'     },
  builder: { name: 'Builder', priceLabel: '£99/mo'     },
  scale:   { name: 'Scale',   priceLabel: '£299/mo'    },
};

const PLAN_FEATURES = {
  free:    ['1,000 bookings/month', '1 calendar connection', 'Venn Links', 'Calendar sync'],
  indie:   ['1,000 bookings/month', '5 calendar connections', 'Paid bookings (Stripe)', 'Webhook delivery', 'Priority support'],
  builder: ['10,000 bookings/month', 'Unlimited calendars', 'SLA support', 'Custom integrations', 'Priority support'],
  scale:   ['50,000 bookings/month', 'Unlimited calendars', 'Dedicated support', 'Custom SLA', 'All Builder features'],
};

const TIER_ORDER = ['free', 'indie', 'builder', 'scale'];

const FEATURES = [
  { label: 'Bookings/month',       free: '1,000',     indie: '1,000',   builder: '10,000',    scale: '50,000'    },
  { label: 'Calendar connections', free: '1',         indie: '5',       builder: 'Unlimited', scale: 'Unlimited' },
  { label: 'Venn Links',           free: true,        indie: true,      builder: true,        scale: true        },
  { label: 'Calendar sync',        free: true,        indie: true,      builder: true,        scale: true        },
  { label: 'Paid bookings',        free: false,       indie: true,      builder: true,        scale: true        },
  { label: 'Webhook delivery',     free: false,       indie: true,      builder: true,        scale: true        },
  { label: 'Priority support',     free: false,       indie: true,      builder: true,        scale: true        },
  { label: 'SLA support',          free: false,       indie: false,     builder: true,        scale: true        },
  { label: 'Custom integrations',  free: false,       indie: false,     builder: true,        scale: true        },
  { label: 'Dedicated support',    free: false,       indie: false,     builder: false,       scale: true        },
];

function FeatureCell({ value }) {
  if (value === true) return <Check size={14} className={styles.cellCheck} aria-label="Included" />;
  if (value === false) return <X size={14} className={styles.cellCross} aria-label="Not included" />;
  return <span>{value}</span>;
}

function getBarColorClass(used, limit) {
  if (!limit) return styles.barGreen;
  const pct = (used / limit) * 100;
  if (pct >= 100) return styles.barRed;
  if (pct >= 80) return styles.barAmber;
  return styles.barGreen;
}

export default function BillingSettings() {
  const navigate = useNavigate();
  const { refetchPlan } = usePlan();
  const [billingStatus, setBillingStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [portaling, setPortaling] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === '1') {
      posthog.capture('upgrade');
      toast.success('Subscription updated!');
      window.history.replaceState({}, '', '/billing');
      refetchPlan();
    }
    loadData();
  }, []);

  async function getAuthHeaders() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { navigate('/auth'); return null; }
    return {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    };
  }

  async function loadData() {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      if (!headers) return;

      setInvoicesLoading(true);
      const [statusRes, usageRes, invoicesRes] = await Promise.all([
        fetch(`${API_URL}/v1/billing/status`, { headers }),
        fetch(`${API_URL}/v1/billing/usage`, { headers }),
        fetch(`${API_URL}/v1/billing/invoices`, { headers }),
      ]);

      if (statusRes.ok) setBillingStatus(await statusRes.json());
      if (usageRes.ok) setUsage(await usageRes.json());
      if (invoicesRes.ok) {
        const data = await invoicesRes.json();
        setInvoices(data.invoices || []);
      }
    } catch (err) {
      console.error('Error loading billing data:', err);
    } finally {
      setLoading(false);
      setInvoicesLoading(false);
    }
  }

  async function handleUpgrade(plan) {
    setUpgrading(plan);
    try {
      const headers = await getAuthHeaders();
      if (!headers) return;

      const res = await fetch(`${API_URL}/v1/billing/checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ plan }),
      });

      if (res.ok) {
        const { url } = await res.json();
        posthog.capture('checkout_started', { plan });
        window.location.href = url;
      } else {
        const data = await res.json();
        toast.error(data.message || 'Failed to start checkout');
      }
    } catch (err) {
      toast.error('Failed to start checkout');
    } finally {
      setUpgrading(null);
    }
  }

  async function handleManage() {
    setPortaling(true);
    try {
      const headers = await getAuthHeaders();
      if (!headers) return;

      const res = await fetch(`${API_URL}/v1/billing/portal`, {
        method: 'POST',
        headers,
      });

      if (res.ok) {
        const { url } = await res.json();
        window.location.href = url;
      } else {
        toast.error('Failed to open billing portal');
      }
    } catch (err) {
      toast.error('Failed to open billing portal');
    } finally {
      setPortaling(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.loadingPage}>
        <span className={styles.spinner} aria-label="Loading" />
      </div>
    );
  }

  const currentPlanKey = billingStatus?.plan?.name || 'free';
  const currentPlan = PLANS[currentPlanKey] || PLANS.free;
  const currentTierIndex = TIER_ORDER.indexOf(currentPlanKey);

  return (
    <div className={styles.page}>
      {/* Top bar */}
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <div className={styles.topBarLeft}>
            <Link to="/dashboard" className={styles.backLink}>
              <ArrowLeft size={16} />
              Back to Dashboard
            </Link>
            <h1 className={styles.pageTitle}>
              <CreditCard size={32} className={styles.pageTitleIcon} aria-hidden="true" />
              Billing
            </h1>
            <p className={styles.pageSubtitle}>
              Manage your plan, usage, and subscription
            </p>
          </div>
        </div>
      </div>

      <div className={styles.main}>

        {/* Section 1: Current Plan */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Current Plan</h2>
          <div className={styles.planCard}>
            <div className={styles.planCardTop}>
              <div>
                <div className={styles.planNameRow}>
                  <span className={`${styles.planBadge} ${styles[`planBadge_${currentPlanKey}`]}`}>
                    {currentPlan.name}
                  </span>
                  <span className={styles.planPrice}>{currentPlan.priceLabel}</span>
                </div>
                {billingStatus?.subscription?.current_period_end && currentPlanKey !== 'free' && (
                  <p className={styles.planRenewal}>
                    Next billing date:{' '}
                    {new Date(billingStatus.subscription.current_period_end).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'long', year: 'numeric',
                    })}
                  </p>
                )}
              </div>
              <div className={styles.planActions}>
                {currentPlanKey === 'free' && (
                  <button
                    className={styles.primaryBtn}
                    onClick={() => handleUpgrade('indie')}
                    disabled={upgrading === 'indie'}
                  >
                    {upgrading === 'indie' ? 'Redirecting…' : 'Upgrade to Indie'}
                  </button>
                )}
                {currentPlanKey === 'indie' && (
                  <>
                    <button
                      className={styles.primaryBtn}
                      onClick={() => handleUpgrade('builder')}
                      disabled={upgrading === 'builder'}
                    >
                      {upgrading === 'builder' ? 'Redirecting…' : 'Upgrade to Builder'}
                    </button>
                    <button
                      className={styles.outlineBtn}
                      onClick={handleManage}
                      disabled={portaling}
                    >
                      {portaling ? 'Opening…' : 'Manage Subscription'}
                    </button>
                  </>
                )}
                {currentPlanKey === 'builder' && (
                  <button
                    className={styles.outlineBtn}
                    onClick={handleManage}
                    disabled={portaling}
                  >
                    {portaling ? 'Opening…' : 'Manage Subscription'}
                  </button>
                )}
                {currentPlanKey === 'scale' && (
                  <a href="mailto:matt@whisker1.com" className={styles.outlineBtn}>
                    Contact Support
                  </a>
                )}
              </div>
            </div>
            <ul className={styles.planFeatureList}>
              {(PLAN_FEATURES[currentPlanKey] || []).map(f => (
                <li key={f} className={styles.planFeatureItem}>
                  <Check size={14} className={styles.planFeatureCheck} aria-hidden="true" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Section 2: Usage */}
        {usage && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Usage this month</h2>
            <div className={styles.usageCard}>
              <div className={styles.usageRow}>
                <div className={styles.usageLabels}>
                  <span className={styles.usageLabel}>Bookings</span>
                  <span className={styles.usageNumbers}>
                    {(usage.bookings?.used || 0).toLocaleString()} /{' '}
                    {usage.bookings?.limit ? usage.bookings.limit.toLocaleString() : '∞'}
                  </span>
                </div>
                {usage.bookings?.limit ? (
                  <div className={styles.barTrack}>
                    <div
                      className={`${styles.barFill} ${getBarColorClass(usage.bookings?.used || 0, usage.bookings.limit)}`}
                      style={{ width: `${Math.min(100, ((usage.bookings?.used || 0) / usage.bookings.limit) * 100)}%` }}
                    />
                  </div>
                ) : (
                  <div className={styles.barTrack}>
                    <div className={`${styles.barFill} ${styles.barGreen}`} style={{ width: '100%' }} />
                  </div>
                )}
              </div>
              <div className={styles.usageRow}>
                <div className={styles.usageLabels}>
                  <span className={styles.usageLabel}>Calendars connected</span>
                  <span className={styles.usageNumbers}>
                    {usage.calendars?.connected || 0} /{' '}
                    {usage.calendars?.limit ? usage.calendars.limit : 'Unlimited'}
                  </span>
                </div>
                {usage.calendars?.limit ? (
                  <div className={styles.barTrack}>
                    <div
                      className={`${styles.barFill} ${getBarColorClass(usage.calendars?.connected || 0, usage.calendars.limit)}`}
                      style={{ width: `${Math.min(100, ((usage.calendars?.connected || 0) / usage.calendars.limit) * 100)}%` }}
                    />
                  </div>
                ) : (
                  <div className={styles.barTrack}>
                    <div className={`${styles.barFill} ${styles.barGreen}`} style={{ width: '30%' }} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Section 3: Invoice History */}
        {(invoicesLoading || invoices.length > 0) && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Invoice history</h2>
            {invoicesLoading ? (
              <p className={styles.invoicesLoading}>Loading invoices…</p>
            ) : (
              <div className={styles.invoiceList}>
                {invoices.map(inv => (
                  <div key={inv.id} className={styles.invoiceRow}>
                    <div className={styles.invoiceMeta}>
                      <span className={styles.invoiceNumber}>{inv.number || inv.id}</span>
                      <span className={styles.invoiceDate}>
                        {new Date(inv.created * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <div className={styles.invoiceRight}>
                      <span className={styles.invoiceAmount}>
                        {new Intl.NumberFormat('en-GB', { style: 'currency', currency: (inv.currency || 'gbp').toUpperCase() }).format(inv.amount_due / 100)}
                      </span>
                      <span className={`${styles.invoiceStatus} ${styles[`invoiceStatus_${inv.status}`]}`}>
                        {inv.status}
                      </span>
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className={styles.invoiceDownload}>
                          PDF
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Section 4: Plan Comparison */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Plan comparison</h2>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.thFeature}></th>
                  {TIER_ORDER.map(tier => (
                    <th
                      key={tier}
                      className={`${styles.thPlan} ${tier === currentPlanKey ? styles.thPlanCurrent : ''}`}
                    >
                      <div className={styles.thPlanName}>{PLANS[tier].name}</div>
                      <div className={styles.thPlanPrice}>
                        {tier === 'scale' ? 'Contact' : PLANS[tier].priceLabel}
                      </div>
                      {tier === currentPlanKey && (
                        <span className={styles.currentBadge}>Current</span>
                      )}
                      {tier !== currentPlanKey && TIER_ORDER.indexOf(tier) > currentTierIndex && tier !== 'scale' && (
                        <button
                          className={styles.tableUpgradeBtn}
                          onClick={() => handleUpgrade(tier)}
                          disabled={!!upgrading}
                        >
                          {upgrading === tier ? '…' : 'Upgrade'}
                        </button>
                      )}
                      {tier === 'scale' && tier !== currentPlanKey && TIER_ORDER.indexOf(tier) > currentTierIndex && (
                        <a href="mailto:matt@whisker1.com" className={styles.tableUpgradeBtn}>
                          Contact
                        </a>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURES.map(feature => (
                  <tr key={feature.label} className={styles.tr}>
                    <td className={styles.tdFeature}>{feature.label}</td>
                    {TIER_ORDER.map(tier => (
                      <td
                        key={tier}
                        className={`${styles.tdPlan} ${tier === currentPlanKey ? styles.tdPlanCurrent : ''}`}
                      >
                        <FeatureCell value={feature[tier]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
